import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import { userHasPermission, PERMISSIONS } from './permissions';
import {
  USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_DETAIL,
  USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_HORIZON,
  USER_PREFERENCES_INITIAL_BENCHMARK,
} from '@/redux/modules/user-preferences';
import SharingInfo from '@/types/sharing-info';
import User from '@/types/user';
import UserPreferencesComparisonSettings from '@/types/user-preferences-comparison-settings';
import AppRegion from '@/types/app-region';
import { PreferredDataProvider } from '@/types/app-config';
import { DATA_PROVIDER_NAME } from '@/enums/DataProvider';
import { validateAppMode, validateAppRegion } from './enterprise';
import AppMode from '@/types/app-mode';

export const formatUserAddress = (user: User) => {
  const addressData = [user.address1, user.address2, user.city, user.postalCode, user.country];
  return addressData.reduce((acc, val) => {
    if (!val || !val.length) {
      return acc;
    }
    if (!acc) {
      return val;
    }
    return `${acc}, ${val}`;
  });
};

export enum USER_ONBOARDING {
  PASSWORD = 'password',
  CONTACT = 'contact',
  PHOTO = 'photo',
  DONE = 'done',
}

export const onboardingIsDone = (user: User) => {
  const { lname, fname, company } = user;
  return fname && lname && company;
};

/**
 * Get sharing info for a user
 * @param user the user model to retrieve sharing info for
 * @returns {SharingInfo} returns the sharingInfo of a user
 */
export const getSharingInfo = (user: User) => {
  const { id, fname, lname } = user;
  const sharingInfo: SharingInfo = { id, company: user.enterprise?.name, name: `${fname} ${lname}` };
  return sharingInfo;
};

/**
 * Get the initial user preferences
 * @param user the authenticated user info to validate the permissions
 * @returns {UserPreferencesComparisonSettings} returns a default list of preferences
 */
export const getInitialUserPreferences = (user?: User | null): UserPreferencesComparisonSettings => ({
  initialBenchmark: userHasPermission(user, PERMISSIONS.COMPARISON_AUTO_BENCHMARK)
    ? USER_PREFERENCES_INITIAL_BENCHMARK.CATEGORY_BENCHMARK
    : USER_PREFERENCES_INITIAL_BENCHMARK.NO_BENCHMARK,
  performanceAndRiskData: PERIOD_END_DATA_OPTIONS.NONE,
  hypotheticalReturnChartDetail: USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_DETAIL.WEEKLY,
  hypotheticalReturnChartHorizon: USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_HORIZON['3Y'],
  baseCurrency: false,
});

export enum DATA_PROVIDER {
  FACTSET = 'FACTSET',
  PREQIN = 'PREQIN',
  REFINITIV_US = 'REFINITIV_US',
  REFINITIV_CA = 'REFINITIV_CA',
}

export const getDataProviderForDisplay = (user: User) => {
  if (user.appConfig.search?.FUNDS?.data_providers.includes(DATA_PROVIDER.FACTSET)) {
    return DATA_PROVIDER.FACTSET;
  }
  if (user.appConfig.appRegion === AppRegion.US && user.appConfig.search?.FUNDS?.data_providers.includes(DATA_PROVIDER.REFINITIV_US)) {
    return DATA_PROVIDER.REFINITIV_US;
  }
  return DATA_PROVIDER.REFINITIV_CA;
};

/**
 * Get preferred data provider for the user based on the data field
 * @param user the authenticated user
 * @param field the data field that uses a preferred data provider - example - fundPortfolio.investmentInfo.capitalization is for Market Data in Equity Style Breakdown
 * @returns {PreferredDataProvider|undefined} returns the preferred data provider for the user and field if exists else undefined
 */
export const getPreferredDataProvider = (user: User, field: string): PreferredDataProvider | undefined =>
  user.appConfig.preferredDataProviders.find(provider => provider.key === field);

/**
 * Verifies if the preferredDataProvider is REFINITIV
 * @param preferredProvider the prefeeredDataProvider
 * @returns {boolean} returns true if the preferredDataProvider is either REFINITIV_US or REFINITIV_CA
 */
export const isPreferredProviderRefinitiv = (preferredProvider: PreferredDataProvider | undefined): boolean =>
  preferredProvider?.provider === DATA_PROVIDER.REFINITIV_CA || preferredProvider?.provider === DATA_PROVIDER.REFINITIV_US;

/**
 * Determines the preferred data provider based on the user's preferences. For Factset users, it defaults to FACTSET.
 * If the user has a preferred data provider set for the field, it returns that. Otherwise, it defaults to FUNDATA.
 * @param {User} user - the authenticated user
 * @param {string} field - the data field that uses a preferred data provider - example - fundPortfolio.investmentInfo.peRatio in Equity Metrics
 * @returns preferred data provider name based on the user's preference and the field provided
 */
export const getDataProviderName = (user: User, field: string) => {
  const isAppModeFactset = validateAppMode(user.appConfig.mode, AppMode.FACT_SET);
  if (isAppModeFactset) {
    return DATA_PROVIDER_NAME.FACTSET;
  }
  const preferredProvider = getPreferredDataProvider(user, field);

  switch (preferredProvider?.provider) {
    case DATA_PROVIDER.PREQIN: {
      return DATA_PROVIDER_NAME.PREQIN;
    }
    case DATA_PROVIDER.REFINITIV_CA: {
      return DATA_PROVIDER_NAME.REFINITIV;
    }
    case DATA_PROVIDER.REFINITIV_US: {
      return DATA_PROVIDER_NAME.REFINITIV;
    }
    case DATA_PROVIDER.FACTSET: {
      return DATA_PROVIDER_NAME.FACTSET;
    }

    default: {
      return DATA_PROVIDER_NAME.FUNDATA;
    }
  }
};

/**
 * Get the user's app region.
 * Returns undefined if user is null/undefined or doesn't have appConfig/appRegion.
 * @param user user object
 */
export const getUserAppRegion = (user: User | null | undefined): AppRegion | undefined => {
  return user?.appConfig?.appRegion;
};

/**
 * Validate a user's app region against an expected region.
 * Handles cases where user is undefined/null or doesn't have appConfig or appRegion defined.
 * @param user user object
 * @param expectedRegion region to compare against
 */
export const validateUserAppRegion = (user: User | null | undefined, expectedRegion: AppRegion): boolean => {
  const userAppRegion = getUserAppRegion(user);
  if (!userAppRegion) {
    return false;
  }
  return validateAppRegion(userAppRegion, expectedRegion);
};
